import { useQuery } from "@tanstack/react-query";
import { Bell } from "lucide-react";

import { get } from "@/lib/api-client";
import { formatRelative } from "@/lib/format";
import { Card, CardBody } from "@/components/ui/Card";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { Badge } from "@/components/ui/Badge";
import type { Notification } from "@/types/notification";

export function NotificationsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => get<Notification[]>("/notifications/notifications"),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-surface-900">Notifications</h1>
        <p className="text-sm text-surface-500 mt-1">
          Passive updates from every engine — orders, payments, entries, rewards, and more.
        </p>
      </header>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : data && data.length > 0 ? (
            <ul className="divide-y divide-surface-100">
              {data.map((n) => (
                <li key={n.id} className="px-5 py-4 flex items-start justify-between gap-4 hover:bg-surface-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge tone="neutral" className="capitalize">
                        {n.category.replace(/_/g, " ")}
                      </Badge>
                      <span className="text-2xs text-surface-500 uppercase">{n.channel}</span>
                    </div>
                    <div className="text-sm font-semibold text-surface-900">{n.subject}</div>
                    <div className="text-sm text-surface-600 mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="mt-1 text-2xs text-surface-400">{formatRelative(n.created_at)}</div>
                  </div>
                  <StatusPill status={n.status} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={Bell} title="No notifications yet"
                         description="You'll see updates here as activity happens." />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
